import React, { useCallback } from 'react';
import { cloneDeep, EMPTY_FN, forEach, isEmpty, map, remove } from '@libs/shared';
import classNames from 'classnames';
import { ValuePanel } from '@fx-ui/jdy-design-biz';
import { MultiBaseMemberSolidIconSelectedIcon } from '@fx-ui/icon';
import { Tag, Tooltip } from '@fx-ui/fine-design';
import { platformIntl } from '../../../../constants';

export interface IReceiverData {
    value: string; // 标识
    text: string; // 文案
    editable: boolean; // 是否可删除
    email_info?: any; // 是否绑定邮箱
    phone_info?: any; // 是否绑定手机
}

interface IMessageReceiver {
    targetType: string; // 数据类型：site,email,sms
    data: IReceiverData[]; // 数据列表
    edit?: boolean; // 整体是否可编辑
    onChange?: (value: string[], targetType: string) => void; // 删除数据项回调
}

/**
 * 通知管理配置项
 *
 * @param props IMessageReceiver
 * @class
 */
const MessageReceiver: React.FC<IMessageReceiver> = (props) => {
    const { targetType, data, edit = true, onChange = EMPTY_FN } = props;

    /**
     * 获取提示信息
     *
     * @param item 当前item数据
     */
    const getTip = (item: IReceiverData) => {
        const { phone_info, email_info } = item;
        if (item.editable) {
            if (targetType === 'sms') {
                const phone = phone_info && phone_info.phone;
                const phoneVerified = phone_info && phone_info.is_verified;
                if (!phoneVerified) {
                    return platformIntl.get('msg.target.phone.unverified');
                }
                if (isEmpty(phone)) {
                    return platformIntl.get('msg.target.phone.empty');
                }
            } else if (targetType === 'email') {
                const email = email_info && email_info.email;
                const emailVerified = email_info && email_info.is_verified;
                if (!emailVerified) {
                    return platformIntl.get('msg.target.email.unverified');
                }
                if (isEmpty(email)) {
                    return platformIntl.get('msg.target.email.empty');
                }
            }
        } else {
            if (targetType === 'sms') {
                return platformIntl.get('msg.target.tooltip.sms');
            } else if (targetType === 'email') {
                return platformIntl.get('msg.target.tooltip.mail');
            }
        }
        return '';
    };

    /**
     * 是否为有效通知对象
     *
     * @param item 数据
     * @param tip 提示
     */
    const isInvalid = (item: IReceiverData, tip: string) => {
        return item.editable && !isEmpty(tip);
    };

    /**
     * 删除推送对象点击回调
     */
    const handleTagClose = useCallback(
        (value: string) => {
            const dataArr = cloneDeep(data);
            const valArr: string[] = [];
            remove(dataArr, (item) => {
                return item.value === value;
            });
            forEach(dataArr, (item) => {
                const { value } = item;
                if (!isEmpty(value)) {
                    valArr.push(value);
                }
            });
            onChange(valArr, targetType);
        },
        [data]
    );

    return (
        <ValuePanel
            className="fx-message-receiver"
            ellipsis
            items={map(data, (item) => ({
                ...item,
                _id: item.value,
                name: item.text
            }))}
            renderItem={(item) => {
                const { editable, text, value } = item;
                const tip = getTip(item);

                const tagEle = (
                    <Tag
                        key={value}
                        icon={
                            !editable ? (
                                <MultiBaseMemberSolidIconSelectedIcon className="target-icon" />
                            ) : undefined
                        }
                        className={classNames('message-receiver-tag', {
                            invalid: isInvalid(item, tip)
                        })}
                        closable={editable && edit}
                        onClose={() => {
                            handleTagClose(value);
                        }}
                        shape="roundedRec"
                    >
                        {text}
                    </Tag>
                );
                if (isEmpty(tip)) {
                    return tagEle;
                }
                return (
                    <Tooltip
                        key={value}
                        style={{ maxWidth: 200 }}
                        effect="light"
                        content={tip}
                        placement="top"
                    >
                        {tagEle}
                    </Tooltip>
                );
            }}
            ellipsisTooltip={{
                placement: 'top-right'
            }}
        />
    );
};

export default MessageReceiver;
